import Link from 'next/link'
import {Fragment} from 'react'
import Container from '@helios/Container'
import FAQAccordion from '@helios/FAQAccordion'
import FAQSection from '@helios/FAQSection'
import styles from './SiteFAQAccordion.module.scss'

function renderSegment(segment, index) {
  if (typeof segment === 'string') {
    return <Fragment key={index}>{segment}</Fragment>
  }

  let content = segment.text

  if (segment.emphasis) {
    content = <em>{content}</em>
  }

  if (segment.strong) {
    content = <strong>{content}</strong>
  }

  if (segment.href) {
    if (segment.external) {
      return (
        <a key={index} href={segment.href} target="_blank" rel="noreferrer">
          {content}
        </a>
      )
    }

    return (
      <Link key={index} href={segment.href}>
        {content}
      </Link>
    )
  }

  return <span key={index}>{content}</span>
}

function renderParagraph(paragraph, index) {
  return <p key={index}>{paragraph.map(renderSegment)}</p>
}

/**
 * The answer renderer for the site FAQ data (`utils/siteFaqs.js`): a `body`
 * of paragraphs, each a list of strings and `{text, href, external, strong,
 * emphasis}` segments. Exported so a page whose FAQ data has the same shape
 * renders its answers the same way (Phase 5 Q5: /transfer).
 */
export function renderFAQAnswer(body) {
  return <>{body.map(renderParagraph)}</>
}

function faqItems(faqs) {
  return faqs.map(faq => ({
    question: faq.question,
    answer: renderFAQAnswer(faq.body),
  }))
}

function SiteFAQAccordion({faqs}) {
  return <FAQAccordion items={faqItems(faqs)} />
}

const GRID_CLASS_NAMES = {
  '5-7': styles['site-faq__grid--5-7'],
  '6-6': styles['site-faq__grid--6-6'],
}

/**
 * The site FAQ band (Phase 5 PR 5.4): FAQSection around the legacy two-column
 * grid, the "FAQ" title (a paragraph, as the legacy `div.title` was, on the
 * section display role) and the accordion. `faqs` takes the site FAQ data,
 * which also feeds the page's FAQ JSON-LD (`utils/seo/siteSchemas.js`);
 * `items` takes FAQAccordion items for a list that has no such data.
 * `children` render after the accordion (a disclaimer). `columns` keeps the
 * page's legacy split from lg (`5-7` or `6-6`); `gutters` is the Container's.
 *
 * @param {{
 *   children?: import('react').ReactNode,
 *   columns?: '5-7' | '6-6',
 *   faqs?: ReadonlyArray<{question: string, body: ReadonlyArray<ReadonlyArray<unknown>>}>,
 *   gutters?: 'default' | 'editorial',
 *   items?: ReadonlyArray<{question: string, answer: import('react').ReactNode}>,
 *   title?: string,
 * }} props
 */
export function SiteFAQSection({
  children,
  columns = '5-7',
  faqs,
  gutters = 'default',
  items,
  title = 'FAQ',
}) {
  return (
    <FAQSection>
      <Container gutters={gutters}>
        <div
          className={`${styles['site-faq__grid']} ${GRID_CLASS_NAMES[columns]}`}
        >
          <div className={styles['site-faq__header']}>
            <p className={styles['site-faq__title']}>{title}</p>
          </div>
          <div className={styles['site-faq__content']}>
            <FAQAccordion items={items ?? faqItems(faqs)} />
            {children}
          </div>
        </div>
      </Container>
    </FAQSection>
  )
}

export default SiteFAQAccordion
